import Decimal from 'decimal.js';
import { noop } from 'lodash';
import { createContext } from 'react';
import * as React from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntityRichSection,
  EditEntitySection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { SubformConfig } from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import { EntityType } from '@/modules/entities/types/EntityType';

// Define possible submit button types
export type SubmitButtonType =
  | 'save'
  | 'prev'
  | 'next'
  | 'section-switcher'
  | null;

export interface EditEntitySplitScreenModalContext {
  entityType: EntityType | undefined;

  // The current entity section that the user is editing.
  currentSection: EditEntitySection;
  setCurrentSection: (section: EditEntitySection) => void;
  isDPSection: boolean;
  isBDGSection: boolean;

  // Track which button was clicked during form submission
  clickedSubmitButton: SubmitButtonType;
  setClickedSubmitButton: (button: SubmitButtonType) => void;

  // The SubformConfig of the current section.
  currentSectionSubformConfig: SubformConfig | undefined;

  // The initial data for the whole entity (all sections).
  // Even though we just show one section/subform at a time, some subforms need
  // data from other subforms.
  initialSubformValues: CombinedSupportedSubformFieldTypes | undefined;

  // The available entity sections to edit for the entity type.
  sectionOptions: EditEntityRichSection[];

  // Indicates if there was an error fetching the entity data (from useData hook).
  hasError: boolean;

  // The ref to the footer of the modal, where the cancel/save buttons will be portaled to.
  // But the cancel/save buttons need to live in EditEntitySplitScreenFormInner,
  // because they rely on the form context.
  footerRef: React.RefObject<HTMLDivElement | null>;

  // The ref to the header of the modal, where the section switcher will be portaled to.
  // Like the footer, the section switcher needs to live in EditEntitySplitScreenFormInner,
  // because it relies on the form context.
  headerRef: React.RefObject<HTMLDivElement | null>;

  // When we're editing dispositive provisions from a scenario where we have a projected
  // entity value, like from a waterfall, we show the projected value in the dispositive
  // provisions section of the modal
  entityValueOverride?: Decimal;

  handleCloseModal: (skipChecks?: boolean) => void;

  // Called after the entity is successfully saved (any section). Lets a host —
  // e.g. the presentation designer — refresh its own data on save rather than
  // on close, so a pure cancel doesn't trigger a needless refetch.
  onAfterSave?: () => void;

  // Refs to portal duplicates to after committing suggestion matches to the forms.
  duplicatesRefs: Partial<
    Record<
      AISuggestionsMatcherArrayField,
      React.RefObject<HTMLDivElement | null>
    >
  >;
}

export const getDefaultValues = (): EditEntitySplitScreenModalContext => ({
  entityType: undefined,
  currentSection: EditEntitySection.BASIC_INFORMATION,
  setCurrentSection: noop,
  isDPSection: false,
  isBDGSection: false,
  clickedSubmitButton: null,
  setClickedSubmitButton: noop,
  currentSectionSubformConfig: undefined,
  initialSubformValues: undefined,
  sectionOptions: [],
  hasError: false,
  footerRef: React.createRef<HTMLDivElement>(),
  headerRef: React.createRef<HTMLDivElement>(),
  handleCloseModal: noop,
  duplicatesRefs: {},
});

export const EditEntitySplitScreenModalContext =
  createContext<EditEntitySplitScreenModalContext>(getDefaultValues());

export const useEditEntitySplitScreenModalContext = () => {
  return useGuardedContext(
    EditEntitySplitScreenModalContext,
    'EditEntitySplitScreenModalContextProvider'
  );
};
